/**
 * M-D9 — hour classes, for payroll and for the client's price, in one place.
 *
 * Two different questions share one calendar:
 *
 *   - PAYROLL (`classifyWorkIntervals`): which of a worker's hours are
 *     regular / overtime / night / weekend / public holiday. Holidays are the
 *     WORKER's payroll country's (default EE), never the site's (Marti
 *     2026-10-22). Moved here unchanged from functions' classifyHours.ts,
 *     which re-exports it — pay does not change.
 *   - CLIENT (`clientHourClassAt`): which class a billed minute falls in for
 *     the client's coefficient. Holidays are the OBJECT's country (O-6,
 *     default EE), the zone is the object's. Priority public holiday >
 *     weekend > night > regular, ONE class per minute, coefficients never
 *     multiplied together (Marti Q3). No client overtime class in v1 (O-5).
 *
 * UTC instants, site-local wall time: a repeated autumn minute counts twice
 * as real time, a skipped spring minute never exists (DST).
 *
 * Pure. Functions import it by relative path; the browser through the package.
 */
import type { CountryCode } from './client';
import type { BillingHourClass } from './invoice';
import { publicHolidayDates } from './publicHolidays';
import type { TimesheetHourBreakdown } from './timesheet';
import { DEFAULT_WORK_TIME_ZONE, wallTime } from './workCalendar';
import type { WorkInterval } from './workIntervals';

export function classifyWorkIntervals(
  intervals: WorkInterval[],
  timeZone = DEFAULT_WORK_TIME_ZONE,
  /** The worker's payroll country; callers that do not know it get EE. */
  payrollCountry: CountryCode = 'EE',
): TimesheetHourBreakdown {
  const holidaysByYear = new Map<number, Set<string>>();
  const breakdown: TimesheetHourBreakdown = {
    regularHours: 0,
    overtimeHours: 0,
    nightHours: 0,
    weekendHours: 0,
    publicHolidayHours: 0,
  };

  const regularByDay = new Map<string, number>();
  // Iterate UTC minute boundaries: repeated autumn minutes remain distinct,
  // nonexistent spring minutes never exist. Preserve fractional endpoint minutes.
  for (const interval of intervals) {
    for (let at = interval.start; at < interval.end; ) {
      const end = Math.min(interval.end, (Math.floor(at / 60000) + 1) * 60000);
      const local = wallTime(at, timeZone);
      const day = local.slice(0, 10);
      const year = Number(day.slice(0, 4));
      let holidays = holidaysByYear.get(year);
      if (!holidays) {
        holidays = publicHolidayDates(payrollCountry, year);
        holidaysByYear.set(year, holidays);
      }
      const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
      const hour = Number(local.slice(11, 13));
      const hours = (end - at) / 3600000;
      if (holidays.has(day)) breakdown.publicHolidayHours += hours;
      else if (weekday === 0 || weekday === 6) breakdown.weekendHours += hours;
      else if (hour >= 22 || hour < 6) breakdown.nightHours += hours;
      else regularByDay.set(day, (regularByDay.get(day) ?? 0) + hours);
      at = end;
    }
  }

  // Pass 2 — split regular per day into regular + overtime (>8h).
  for (const dayHours of regularByDay.values()) {
    const reg = Math.min(8, dayHours);
    const ot = Math.max(0, dayHours - 8);
    breakdown.regularHours += reg;
    breakdown.overtimeHours += ot;
  }

  // Round to 2 decimals to keep totals tidy.
  const r = (n: number) => Math.round(n * 100) / 100;
  breakdown.regularHours = r(breakdown.regularHours);
  breakdown.overtimeHours = r(breakdown.overtimeHours);
  breakdown.nightHours = r(breakdown.nightHours);
  breakdown.weekendHours = r(breakdown.weekendHours);
  breakdown.publicHolidayHours = r(breakdown.publicHolidayHours);

  return breakdown;
}

/** Night for the client's price: 22:00–06:00 site-local (the payroll boundary). */
export const CLIENT_NIGHT_FROM_HOUR = 22;
export const CLIENT_NIGHT_TO_HOUR = 6;

/** What is true of a minute, independently: these overlap (a Saturday night is all of weekend and night). */
export interface HourFacts {
  night: boolean;
  weekend: boolean;
  publicHoliday: boolean;
}

/**
 * The facts of an instant on one calendar (zone + country). Memoises the
 * holiday set per year and the answer per 15-minute block: every real zone
 * offset is a multiple of 15 minutes, so a local hour or date never changes
 * inside a block, and a month of minutes stays cheap.
 */
export function hourFactsOn(
  timeZone: string = DEFAULT_WORK_TIME_ZONE,
  country: CountryCode = 'EE',
): (ms: number) => HourFacts {
  const holidaysByYear = new Map<number, Set<string>>();
  const byBlock = new Map<number, HourFacts>();
  return (ms: number) => {
    const block = Math.floor(ms / 900_000);
    const hit = byBlock.get(block);
    if (hit) return hit;
    const local = wallTime(ms, timeZone);
    const day = local.slice(0, 10);
    const year = Number(day.slice(0, 4));
    let holidays = holidaysByYear.get(year);
    if (!holidays) {
      holidays = publicHolidayDates(country, year);
      holidaysByYear.set(year, holidays);
    }
    const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
    const hour = Number(local.slice(11, 13));
    const facts: HourFacts = {
      night: hour >= CLIENT_NIGHT_FROM_HOUR || hour < CLIENT_NIGHT_TO_HOUR,
      weekend: weekday === 0 || weekday === 6,
      publicHoliday: holidays.has(day),
    };
    byBlock.set(block, facts);
    return facts;
  };
}

/**
 * The client class of a minute. Priority public holiday > weekend > night >
 * regular, one class per minute, coefficients never added or multiplied.
 * `agreed` (Marti's variant B, 2026-11-03): the priority applies only AMONG the
 * classes agreed on the order — with night agreed and weekend not, a night
 * minute on a weekend bills night; a minute with no agreed class is regular.
 * Without `agreed`: the calendar's own class.
 */
export function clientHourClassifier(
  timeZone: string = DEFAULT_WORK_TIME_ZONE,
  holidayCountry: CountryCode = 'EE',
  agreed?: ReadonlySet<BillingHourClass>,
): (ms: number) => BillingHourClass {
  const factsAt = hourFactsOn(timeZone, holidayCountry);
  const counts = (c: BillingHourClass) => !agreed || agreed.has(c);
  return (ms: number) => {
    const f = factsAt(ms);
    if (f.publicHoliday && counts('public_holiday')) return 'public_holiday';
    if (f.weekend && counts('weekend')) return 'weekend';
    if (f.night && counts('night')) return 'night';
    return 'regular';
  };
}

/** Hours of paid time that carry a pay premium. They overlap: never summed into hours. */
export interface PayrollPremiumHours {
  nightHours: number;
  weekendHours: number;
  publicHolidayHours: number;
}

/**
 * M-D9 (Marti 2026-11-03) — for RM24's own cost: the premium hours of paid
 * intervals, per UTC minute, on the worker's payroll calendar. NOT the
 * one-class `classifyWorkIntervals`, which stays what it is: pay premiums are
 * not exclusive — night 22–06 counts on a weekend too, and night and public
 * holiday apply together.
 */
export function payrollPremiumHours(
  intervals: WorkInterval[],
  timeZone: string = DEFAULT_WORK_TIME_ZONE,
  payrollCountry: CountryCode = 'EE',
): PayrollPremiumHours {
  const factsAt = hourFactsOn(timeZone, payrollCountry);
  // Milliseconds, divided once: summing fractions of an hour minute by minute drifts.
  let night = 0;
  let weekend = 0;
  let holiday = 0;
  for (const interval of intervals)
    for (let at = interval.start; at < interval.end; ) {
      const end = Math.min(interval.end, (Math.floor(at / 60000) + 1) * 60000);
      const f = factsAt(at);
      const ms = end - at;
      if (f.night) night += ms;
      if (f.weekend) weekend += ms;
      if (f.publicHoliday) holiday += ms;
      at = end;
    }
  return { nightHours: night / 3600000, weekendHours: weekend / 3600000, publicHolidayHours: holiday / 3600000 };
}

/** The client hour class of one instant (convenience; see `clientHourClassifier`). */
export function clientHourClassAt(
  ms: number,
  timeZone: string = DEFAULT_WORK_TIME_ZONE,
  holidayCountry: CountryCode = 'EE',
): BillingHourClass {
  return clientHourClassifier(timeZone, holidayCountry)(ms);
}

/** Priority order (highest first) — the order lines and rows are listed in reverse. */
export const CLIENT_HOUR_CLASS_PRIORITY: readonly BillingHourClass[] = [
  'public_holiday',
  'weekend',
  'night',
  'regular',
];
